import { Identifier } from '@medplum/fhirtypes';

export const MEDPLUM_API_BASE_URL = 'https://api.medplum.com';

export const BARIENDO_BOTS_SYSTEM = 'http://bariendo.com/bots';
export const BARIENDO_CT_SYSTEM = 'http://bariendo.com/ct';
export const BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM = 'http://bariendo.com/fhir/tag/bariatric-procedure-stage';
/** Marks the consult that carries a patient's Post-consultation row (BAR-2865). */
export const LATEST_CONSULT_TAG_SYSTEM = 'http://bariendo.com/fhir/tag/latest-consult';
export const LATEST_CONSULT_TAG_CODE = 'latest-consult';
/** Marks the missed consult that carries a patient's Consultation no-show row (BAR-2888). */
export const NO_SHOW_CONSULT_TAG_SYSTEM = 'http://bariendo.com/fhir/tag/no-show-consult';
export const NO_SHOW_CONSULT_TAG_CODE = 'no-show-consult';
/** The Intake hub's filter values, on the Patient so queue queries can filter on them (BAR-2794). */
export const PATIENT_FILTER_TAG_SYSTEMS = {
  /** One tag per Patient Navigator on the care team; the code is the Practitioner id. */
  pn: 'http://bariendo.com/fhir/tag/pn',
  /** The `Track`, as `getTrackFromEpisodeOfCare` reads it. */
  track: 'http://bariendo.com/fhir/tag/payment-track',
  /** The market, as `readMarketLocation` reads it. */
  location: 'http://bariendo.com/fhir/tag/market',
  /** `active`, `hold` or `lost`. */
  status: 'http://bariendo.com/fhir/tag/lead-status',
  /** The UTC day the patient signed up, `YYYY-MM-DD` (BAR-2877). Filters, but can't sort. */
  signupDay: 'http://bariendo.com/fhir/tag/signup-day',
  /** `inquiry` or `no-show`: the hub tab a Patient-rooted query lists them on. */
  queue: 'http://bariendo.com/fhir/tag/hub-queue',
  /** The UTC day the stage last changed, `YYYY-MM-DD`, from `procedure-stage-changed-at` (BAR-2951). */
  stageDay: 'http://bariendo.com/fhir/tag/stage-day',
  /** The latest PN consultation Outcome, as a `PN_QUALIFICATION_LEVELS` code (BAR-2975). */
  qualification: 'http://bariendo.com/fhir/tag/qualification',
} as const;
/**
 * The payment status as tags on the Patient, so hub queries can filter on it (BAR-3092). The stage
 * bot writes them with the status; kept out of PATIENT_FILTER_TAG_SYSTEMS, whose bot would strip them.
 */
export const PAYMENT_STATUS_TAG_SYSTEMS = {
  /** The `PaymentStatus` code. */
  status: 'http://bariendo.com/fhir/tag/payment-status',
  /** The Coverage payor's Organization id: the insurer, or the benefit administrator. */
  payor: 'http://bariendo.com/fhir/tag/payor',
} as const;
export const TWILIO_SYSTEM = 'http://twilio.com/';

/**
 * Outbound `<Dial>` outcomes returned by Twilio in `DialCallStatus`.
 * We keep these human messages shared between bots (TwiML `<Say>`) and the provider UI (toast),
 * so copy stays consistent.
 */
export const TWILIO_DIAL_CALL_STATUS_MESSAGE: Record<'busy' | 'no-answer' | 'failed', string> = {
  busy: 'The number you called is busy. Please try again later.',
  'no-answer': "The patient didn't answer. Try again in a few minutes.",
  failed: 'The call could not be completed. Please try again later.',
} as const;
export const PARTICIPATION_MODE_CODESYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode';
export const PRACTITIONER_FHIR_URL = 'http://hl7.org/fhir/Practitioner';
export const CARE_TEAM_PARTICIPANT_ROLE_FHIR_URL = 'http://hl7.org/fhir/participant-role';
export const CONFIDENTIALITY_CODESYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality';
export const ROLE_CODE_CODESYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-RoleCode';
export const RELATED_PERSON_RELATIONSHIP_VALUESET_URL =
  'http://bariendo.com/fhir/ValueSet/relatedperson-relationshiptype';
export const APPOINTMENT_TYPES_SYSTEM = 'http://bariendo.com/fhir/CodeSystem/appointment-types';

/**
 * `system|code` search tokens for appointment-type codes — the form both a
 * search parameter (`appointment-type=a,b`) and a `_filter` clause take.
 */
export function appointmentTypeTokens(codes: readonly string[]): string[] {
  return codes.map((code) => `${APPOINTMENT_TYPES_SYSTEM}|${code}`);
}
// Track / program a patient is enrolled on (EpisodeOfCare.type). See data/codesystem/track.json.
export const TRACK_CODESYSTEM = 'http://bariendo.com/fhir/CodeSystem/track';

/**
 * Acquisition track / program codes from the track CodeSystem, used as
 * EpisodeOfCare.type. See data/codesystem/track.json.
 *
 * Adding a track: the type-checker finds every `Record<Track, …>`, but not the
 * explicit sets — `EMPLOYER_SPONSORED_TRACKS` / `PARTNER_TRACKS`
 * (`pac-inquiry-list.ts`), `EMPLOYER_COVERAGE_TRACK_LABELS` (provider-app
 * `services/patient-fields.ts`) and `TRACK_FILTER_OPTIONS` (provider-app
 * `PatientListPage/services/queueFilters.ts`).
 */
export enum Track {
  D2C = 'd2c',
  CARRUM = 'carrum',
  TRANSCARENT = 'transcarent',
  /**
   * An employer benefit the lead claimed on the eligibility flow that no partner
   * covers — or not yet. Distinct from `d2c` because the conversation is about
   * coverage, and distinct from the partner tracks because nobody has verified
   * it and the lead may still end up self-pay (BAR-2596).
   */
  EMPLOYER = 'employer',
  /**
   * The lead's own health insurance, for a lead with no partner employer — the
   * insurer is the payer, not an employer, so this is not employer coverage.
   * No administrator. The patient is never charged, consultation fee included:
   * billing is manual for now (P-BAR-63). See {@link collectsPayment}.
   */
  INSURANCE = 'insurance',
}

/**
 * Go-to-market display labels for each track (patient-profile GTM pill).
 * `d2c` reads as "Self-pay" (the default channel); partner tracks use their
 * name. Total record so a new Track must declare its label.
 */
export const TrackLabel: Record<Track, string> = {
  [Track.D2C]: 'Self-pay',
  [Track.CARRUM]: 'Carrum',
  [Track.TRANSCARENT]: 'Transcarent',
  // No sponsor to name — that is the whole content of the label. The employer
  // the lead typed reads off EMPLOYER_EXTENSION where a surface has room for it.
  [Track.EMPLOYER]: 'Employer',
  [Track.INSURANCE]: 'Insurance',
};

/**
 * The track as it reads where Orbit describes one patient: the profile header
 * pill, the Intake and billing panel's Track row and the track switch it opens,
 * and the PN card's key facts and collapsed line.
 *
 * Differs from {@link TrackLabel} only for `employer`. A named administrator
 * collapses to its partner track on read (`getTrackFromEpisodeOfCare`), so a
 * patient whose track resolves to `employer` claimed a benefit nobody has
 * verified — mostly older signups whose employer matched no partner, before
 * BAR-2766, and anyone an operator moves there by hand. "Employer" there sat
 * beside the PN card's live check saying "No employer benefit" (BAR-2992). Queue pills keep `TrackLabel`: they draw every
 * employer-sponsored patient, partners included, as `employer`.
 */
export const PatientTrackLabel: Record<Track, string> = {
  ...TrackLabel,
  [Track.EMPLOYER]: 'Unverified employer',
};

// Who administers an employer benefit — a second coding on EpisodeOfCare.type,
// alongside the track one. See data/codesystem/benefit-administrator.json.
// Mirrors bariendo-be's `episode-of-care.constants.ts` — keep in sync (BAR-2602).
export const BENEFIT_ADMINISTRATOR_CODESYSTEM = 'http://bariendo.com/fhir/CodeSystem/benefit-administrator';

/**
 * Codes from the benefit-administrator CodeSystem. Absent on an episode means
 * the employer benefit is claimed but nobody has named the administrator.
 */
export enum BenefitAdministrator {
  CARRUM = 'carrum',
  TRANSCARENT = 'transcarent',
}

// A payor's decision on an employer-benefit pre-authorization (BAR-2593). An extension on
// ClaimResponse, because R4 has no `decision` element (R5 added one) and `outcome` is
// processing status — `error` means the request failed, not that it was denied.
// See data/codesystem/benefit-decision.json.
export const BENEFIT_DECISION_CODESYSTEM = 'http://bariendo.com/fhir/CodeSystem/benefit-decision';

export enum BenefitDecision {
  APPROVED = 'approved',
  DENIED = 'denied',
}

// Closed list of reasons a payor denied an employer benefit (BAR-2593).
// See data/codesystem/benefit-denial-reason.json.
export const BENEFIT_DENIAL_REASON_CODESYSTEM = 'http://bariendo.com/fhir/CodeSystem/benefit-denial-reason';

export enum BenefitDenialReason {
  NOT_ELIGIBLE = 'not-eligible',
  PLAN_EXCLUDES_PROCEDURE = 'plan-excludes-procedure',
  MISSING_INFORMATION = 'missing-information',
  PATIENT_WITHDREW = 'patient-withdrew',
  OTHER = 'other',
}

/** Total record so a new reason must declare its label. */
export const BenefitDenialReasonLabel: Record<BenefitDenialReason, string> = {
  [BenefitDenialReason.NOT_ELIGIBLE]: 'Not eligible',
  [BenefitDenialReason.PLAN_EXCLUDES_PROCEDURE]: 'Plan excludes procedure',
  [BenefitDenialReason.MISSING_INFORMATION]: 'Missing information',
  [BenefitDenialReason.PATIENT_WITHDREW]: 'Patient withdrew',
  [BenefitDenialReason.OTHER]: 'Other',
};

/**
 * How long an approval stays valid when the operator does not say otherwise. Our convention,
 * not a published payor policy — it puts a deadline on the chase. BAR-2595 returns a patient to
 * post-consultation when an approval expires unbooked, so a wrong number regresses patients.
 */
export const BENEFIT_APPROVAL_DEFAULT_VALID_DAYS = 30;

// A coverage step care ops record on the employer or insurance card (BAR-3081). An extension on
// the case's Claim / ClaimResponse. See data/codesystem/coverage-action.json.
export const COVERAGE_ACTION_CODESYSTEM = 'http://bariendo.com/fhir/CodeSystem/coverage-action';

export enum CoverageAction {
  // Employer
  REFERRAL_SENT = 'referral-sent',
  BENEFIT_CONFIRMED = 'benefit-confirmed',
  CASE_FORM_SUBMITTED = 'case-form-submitted',
  APPROVAL_DENIED = 'approval-denied',
  // Insurance
  BENEFIT_VERIFIED = 'benefit-verified',
  QUESTIONNAIRE_RECEIVED = 'questionnaire-received',
  PSYCH_EVALUATION_RECEIVED = 'psych-evaluation-received',
  /** Retired (BAR-3128): no longer offered, kept so earlier steps still read. */
  PCP_CLEARANCE_RECEIVED = 'pcp-clearance-received',
  PRIOR_AUTH_SUBMITTED = 'prior-auth-submitted',
  PRIOR_AUTH_DENIED = 'prior-auth-denied',
  // Both
  NO_BENEFIT = 'no-benefit',
  APPROVED = 'approved',
}

// The prior auth prep items, one Task each. See data/codesystem/prior-auth-requirement.json.
export const PRIOR_AUTH_REQUIREMENT_CODESYSTEM = 'http://bariendo.com/fhir/CodeSystem/prior-auth-requirement';

export enum PriorAuthRequirement {
  QUESTIONNAIRE = 'prior-auth-questionnaire',
  RD_CLEARANCE = 'prior-auth-rd-clearance',
  NP_CLEARANCE = 'prior-auth-np-clearance',
  PSYCH_EVALUATION = 'prior-auth-psych-evaluation',
  /** Retired (BAR-3128): never opened now; kept so sets opened before it still read. */
  PCP_CLEARANCE = 'prior-auth-pcp-clearance',
}

/** The four items a Benefit verified opens, in checklist order (BAR-3128). */
export const PRIOR_AUTH_PREP_ITEMS: readonly PriorAuthRequirement[] = [
  PriorAuthRequirement.QUESTIONNAIRE,
  PriorAuthRequirement.RD_CLEARANCE,
  PriorAuthRequirement.NP_CLEARANCE,
  PriorAuthRequirement.PSYCH_EVALUATION,
];

/**
 * The (track, administrator) pair a display `Track` means to bariendo-be.
 *
 * Orbit's Track values are exactly the used cells of the pair, so the UI
 * keeps picking one value and only the wire format splits (BAR-2602).
 */
export function trackToPair(track: Track): {
  toTrack: Track.D2C | Track.EMPLOYER | Track.INSURANCE;
  administrator?: BenefitAdministrator;
} {
  switch (track) {
    case Track.D2C:
      return { toTrack: Track.D2C };
    case Track.EMPLOYER:
      return { toTrack: Track.EMPLOYER };
    case Track.INSURANCE:
      return { toTrack: Track.INSURANCE };
    case Track.CARRUM:
      return { toTrack: Track.EMPLOYER, administrator: BenefitAdministrator.CARRUM };
    case Track.TRANSCARENT:
      return { toTrack: Track.EMPLOYER, administrator: BenefitAdministrator.TRANSCARENT };
  }
}

/**
 * Whether each track's patients are ever asked to pay. Mirrors bariendo-be's
 * `collectsPayment(track, administrator)`; the backend is the authority.
 *
 * A total record so a new `Track` will not compile until someone decides its
 * answer, rather than defaulting to "charged".
 */
const COLLECTS_PAYMENT: Record<Track, boolean> = {
  [Track.D2C]: true,
  // Self-reported and unverified; most of these leads convert to self-pay.
  [Track.EMPLOYER]: true,
  // The administrator pays for the patient.
  [Track.CARRUM]: false,
  [Track.TRANSCARENT]: false,
  // No administrator, but billed manually for now, consultation fee included.
  [Track.INSURANCE]: false,
};

export function collectsPayment(track: Track): boolean {
  return COLLECTS_PAYMENT[track];
}

/**
 * `Coverage.type` as bariendo-be writes it, one code per track. Mirrors
 * bariendo-be's `coverage.constants.ts` — keep in sync. Self-pay uses the HL7
 * `pay` code instead.
 */
export const COVERAGE_TYPE_CODESYSTEM = 'http://bariendo.com/fhir/CodeSystem/coverage-type';

/** The insurance track's Coverage. Its `payor` is the insurer Organization once signup resolves it (BAR-2905). */
export const INSURANCE_COVERAGE_TYPE_CODE = 'insurance';

/** The 25-min PN consultation (30-min block) — Intake 1.1's self-scheduled consult. */
export const PN_CONSULTATION_CODE = 'pn-consultation-visit';

/**
 * Appointment type codes (from the appointment-types CodeSystem) that count as
 * the initial medical consultation for procedure-stage determination.
 *
 * Both openings count. Under Intake 1.1 every self-scheduled consult is the PN
 * one; the NP consult survives as Care-Intake-booked only. Anything reading this
 * must handle more than one code — see `buildConsultFilter`.
 */
export const CONSULTATION_APPOINTMENT_CODES = ['app-initial-consultation-visit', PN_CONSULTATION_CODE] as const;

/**
 * Appointment type codes that count as a bariatric procedure for
 * procedure-stage determination.
 */
export const PROCEDURE_APPOINTMENT_CODES = [
  'esg-procedure',
  'esg-max-procedure',
  'sis-procedure',
  'tore-regular-procedure',
  'tore-plus-procedure',
  'igb-placement-procedure',
] as const;

export const GROUP_APPOINTMENT_CODES = ['group-visit-with-your-doctor', 'group-support'] as const;

export const TIMELINE_FOCUS_ITEM_QUERY_PARAM = 'focus';
export const TIMELINE_CREATE_ITEM_QUERY_PARAM = 'create';
/** Query param on the patient timeline selecting the right-panel tab (e.g. tasks). */
export const TIMELINE_INFO_TAB_QUERY_PARAM = 'infoTab';
export const ORGANIZATIONS_EXPAND_DRAWER_QUERY_PARAM = 'expand';
/** Query param on `/SMS` to filter or deep-link SMS logs by external phone number (e.g. unknown-number inbox items). */
export const SMS_LOGS_PHONE_QUERY_PARAM = 'phone';
/** Twilio's ceiling for one message body. Every SMS composer in Orbit stops the user here. */
export const SMS_MAX_MESSAGE_LENGTH = 1600;

export const UsTimezoneGroup = {
  ET: 'ET',
  CT: 'CT',
  MT: 'MT',
  MST: 'MST',
  PT: 'PT',
  AKT: 'AKT',
  HT: 'HT',
} as const;

export type UsTimezoneGroup = (typeof UsTimezoneGroup)[keyof typeof UsTimezoneGroup];

export const CANONICAL_US_TIMEZONE_BY_GROUP: Record<UsTimezoneGroup, string> = {
  [UsTimezoneGroup.ET]: 'America/New_York',
  [UsTimezoneGroup.CT]: 'America/Chicago',
  [UsTimezoneGroup.MT]: 'America/Denver',
  [UsTimezoneGroup.MST]: 'America/Phoenix',
  [UsTimezoneGroup.PT]: 'America/Los_Angeles',
  [UsTimezoneGroup.AKT]: 'America/Anchorage',
  [UsTimezoneGroup.HT]: 'Pacific/Honolulu',
};

export const US_TIMEZONE_LABEL_BY_GROUP: Record<UsTimezoneGroup, string> = {
  [UsTimezoneGroup.ET]: 'Eastern Time (ET)',
  [UsTimezoneGroup.CT]: 'Central Time (CT)',
  [UsTimezoneGroup.MT]: 'Mountain Time (MT)',
  [UsTimezoneGroup.MST]: 'Arizona Time (MST)',
  [UsTimezoneGroup.PT]: 'Pacific Time (PT)',
  [UsTimezoneGroup.AKT]: 'Alaska Time (AKT)',
  [UsTimezoneGroup.HT]: 'Hawaii Time (HT)',
};

/**
 * US IANA timezone id → {@link UsTimezoneGroup} (dedupe + friendly labels + canonical resolution).
 * Only zones that match a US “mainland-style” group are listed; territories with different
 * offsets (e.g. Atlantic, Samoa) are omitted so they stay as raw IANA in the UI.
 */
export const US_TIMEZONE_GROUP_BY_IANA: Record<string, UsTimezoneGroup> = {
  // Eastern
  'America/Detroit': UsTimezoneGroup.ET,
  'America/Indiana/Indianapolis': UsTimezoneGroup.ET,
  'America/Indiana/Marengo': UsTimezoneGroup.ET,
  'America/Indiana/Petersburg': UsTimezoneGroup.ET,
  'America/Indiana/Vevay': UsTimezoneGroup.ET,
  'America/Indiana/Vincennes': UsTimezoneGroup.ET,
  'America/Indiana/Winamac': UsTimezoneGroup.ET,
  'America/Indianapolis': UsTimezoneGroup.ET,
  'America/Kentucky/Louisville': UsTimezoneGroup.ET,
  'America/Kentucky/Monticello': UsTimezoneGroup.ET,
  'America/Louisville': UsTimezoneGroup.ET,
  'America/New_York': UsTimezoneGroup.ET,
  // Central
  'America/Chicago': UsTimezoneGroup.CT,
  'America/Indiana/Knox': UsTimezoneGroup.CT,
  'America/Indiana/Tell_City': UsTimezoneGroup.CT,
  'America/Menominee': UsTimezoneGroup.CT,
  'America/North_Dakota/Beulah': UsTimezoneGroup.CT,
  'America/North_Dakota/Center': UsTimezoneGroup.CT,
  'America/North_Dakota/New_Salem': UsTimezoneGroup.CT,
  // Mountain (DST)
  'America/Boise': UsTimezoneGroup.MT,
  'America/Denver': UsTimezoneGroup.MT,
  // Arizona (no DST) — canonical Phoenix
  'America/Phoenix': UsTimezoneGroup.MST,
  // Pacific
  'America/Los_Angeles': UsTimezoneGroup.PT,
  // Alaska
  'America/Adak': UsTimezoneGroup.AKT,
  'America/Anchorage': UsTimezoneGroup.AKT,
  'America/Juneau': UsTimezoneGroup.AKT,
  'America/Metlakatla': UsTimezoneGroup.AKT,
  'America/Nome': UsTimezoneGroup.AKT,
  'America/Sitka': UsTimezoneGroup.AKT,
  'America/Yakutat': UsTimezoneGroup.AKT,
  // Hawaii
  'Pacific/Honolulu': UsTimezoneGroup.HT,
};

export const VOICE_RECORDING_PAYLOAD_ID = 'voice-recording';
export const TEXT_CONTENT_PAYLOAD_ID = 'text-content';
/** Structured answers for the PAC intro-call card, stored as JSON. */
export const PAC_INTRO_CALL_ANSWERS_PAYLOAD_ID = 'ci-intro-call-answers';
/** Structured answers for the PAC warm hand-off card, stored as JSON. */
export const PAC_WARM_HANDOFF_ANSWERS_PAYLOAD_ID = 'ci-warm-handoff-answers';
export const RICH_TEXT_CONTENT_PAYLOAD_ID = 'prose-mirror-rich-text-json-content';
export const DOCUMENT_ATTACHMENT_PAYLOAD_ID = 'media-attachment';
export const VIRTUAL_RECEPTIONIST_PAYLOAD_ID = 'virtual-receptionist';

/**
 * `Provenance.reason` codes for events recorded *against* a resource rather than
 * as a field on it.
 *
 * Note there is no `reason` search param on Provenance in R4 — only `target`,
 * `agent`, `recorded`, `patient` and friends. A consumer filters on `target` (or
 * `recorded` for a date range) and checks the reason itself; a Subscription
 * cannot filter on it at all.
 */
export const ProvenanceReason = {
  /** A Patient Access call card was saved to the chart. */
  PAC_CALL_COMPLETED: 'ci-call-completed',
  /** The patient's insurance card was edited through `update-insurance-coverage` (BAR-2917). */
  INSURANCE_COVERAGE_UPDATED: 'insurance-coverage-updated',
  /** A Patient profile field was confirmed or corrected on the PN consultation card (BAR-2928). */
  PATIENT_PROFILE_UPDATED: 'patient-profile-updated',
} as const;

export type ProvenanceReason = (typeof ProvenanceReason)[keyof typeof ProvenanceReason];

export const PATIENT_BREAK_GLASS_TAG = 'break-glass-flag';

// Marks a DocumentReference as a chat attachment so the patient AccessPolicy can
// grant patients read access to their own chat attachments without exposing other
// (e.g. clinician-uploaded) documents that also live in the patient compartment.
export const CHAT_ATTACHMENT_TAG = 'chat-attachment';

// SNOMED Codes, except EMERSON_QUALITY_AUDITOR, FINANCE, RESEARCH_FELLOW and MARKETING: no SNOMED
// occupation concept fits a hospital quality auditor, a finance analyst, a research fellow or a
// marketer, so they use BARIENDO_CT_SYSTEM codes.
// getPrimaryProviderRole compares coding[0].code only and never checks the system, so these
// resolve normally.
export const ProviderRole = {
  ADMINISTRATOR: '308050009',
  PATIENT_COORDINATOR: '768820003', // CC (Care Coordinator; was PC / Patient Coordinator)
  PATIENT_NAVIGATOR: '768836001', // PN
  BEHAVIORAL_EXPERT: '310176003', // BE
  PHYSICIAN: '309343006', // MD or DO
  NURSE_PRACTITIONER: '224571005', // NP
  REGISTERED_DIETITIAN: '159033005', // RD
  CARE_MANAGER: '309446002', // CM or NN
  SUPERVISING_PHYSICIAN: '405279007', // MD-S - Attending physician
  PATIENT_ACCESS: '768821004', // PAC (Patient Access; was PAC / Patient Access) - Care team coordinator (occupation)
  EMERSON_QUALITY_AUDITOR: 'emerson-quality-auditor', // Read-only chart access for Emerson's MBSAQIP audit
  FINANCE: 'finance', // Billing and reimbursement, no clinical record (BAR-2648)
  RESEARCH_FELLOW: 'research-fellow', // Read-only chart access for a time-boxed research fellowship
  MARKETING: 'marketing', // Reads the Intake hub, no clinical record (BAR-2813)
} as const;

export type ProviderRole = (typeof ProviderRole)[keyof typeof ProviderRole];

/**
 * SNOMED role codes allowed as performer on clinical (orbit-provider-task) tasks —
 * the "Roles" group of the task assignee picker. Every role here must hold a
 * policy that reads `orbit-provider-task` Tasks, or work routed to it vanishes
 * (BAR-2585 added Patient Access and its grant together). Physicians stay out on
 * purpose (see create-clinical-task.test).
 */
export const PROVIDER_TASK_PERFORMER_ROLES: ProviderRole[] = [
  ProviderRole.NURSE_PRACTITIONER,
  ProviderRole.PATIENT_COORDINATOR,
  ProviderRole.CARE_MANAGER,
  ProviderRole.PATIENT_NAVIGATOR,
  ProviderRole.PATIENT_ACCESS,
  ProviderRole.REGISTERED_DIETITIAN,
];

/**
 * Roles that work the patient relationship, not the chart: they see
 * conversations, appointments and tasks, and nothing clinical — no encounter
 * notes, prescriptions, orders or results (BAR-2585). The access policy is the
 * real boundary; this list only keeps Orbit from drawing panels and actions
 * the policy would refuse.
 */
export const CLINICAL_RECORD_HIDDEN_ROLES: ProviderRole[] = [
  ProviderRole.PATIENT_ACCESS,
  ProviderRole.FINANCE,
  ProviderRole.MARKETING,
];

/**
 * Roles that must not read patient conversations — calls, SMS, chat, notes —
 * the Communication record the patient timeline is built from (BAR-2648).
 * As with CLINICAL_RECORD_HIDDEN_ROLES, FinanceAccessPolicy is the boundary;
 * this keeps Orbit from issuing the query, which would otherwise fail and take
 * the whole timeline (payment cards included) down with it.
 */
export const CONVERSATIONS_HIDDEN_ROLES: ProviderRole[] = [ProviderRole.FINANCE];

export const PROVIDER_ROLE_LABELS: Record<keyof typeof ProviderRole, string> = {
  ADMINISTRATOR: 'Administrator',
  PATIENT_COORDINATOR: 'Care Coordinator',
  PATIENT_NAVIGATOR: 'Patient Navigator',
  BEHAVIORAL_EXPERT: 'Behavioral Expert',
  PHYSICIAN: 'Physician',
  NURSE_PRACTITIONER: 'Nurse Practitioner',
  REGISTERED_DIETITIAN: 'Registered Dietitian',
  CARE_MANAGER: 'Care Manager',
  SUPERVISING_PHYSICIAN: 'Supervising Physician',
  PATIENT_ACCESS: 'Patient Access',
  EMERSON_QUALITY_AUDITOR: 'Emerson Quality Auditor',
  FINANCE: 'Finance',
  RESEARCH_FELLOW: 'Research Fellow',
  MARKETING: 'Marketing',
};

export type ProviderRoleOption = { value: ProviderRole; label: string };

/**
 * Short role codes for role / team avatars (BAR-2571). Explicit rather than
 * derived from the label: "Patient Access" must read PAC, not PA, and
 * "Supervising Physician" is MD-S, not SP.
 */
export const PROVIDER_ROLE_ABBREVIATIONS: Record<keyof typeof ProviderRole, string> = {
  ADMINISTRATOR: 'ADMIN',
  PATIENT_COORDINATOR: 'CC',
  PATIENT_NAVIGATOR: 'PN',
  BEHAVIORAL_EXPERT: 'BE',
  PHYSICIAN: 'MD',
  NURSE_PRACTITIONER: 'NP',
  REGISTERED_DIETITIAN: 'RD',
  CARE_MANAGER: 'CM',
  SUPERVISING_PHYSICIAN: 'MD-S',
  PATIENT_ACCESS: 'PAC',
  EMERSON_QUALITY_AUDITOR: 'EQA',
  FINANCE: 'FIN',
  RESEARCH_FELLOW: 'RF',
  MARKETING: 'MKT',
};

/** code → enum key, built once; every avatar render reads it. */
const PROVIDER_ROLE_KEY_BY_CODE: ReadonlyMap<ProviderRole, keyof typeof ProviderRole> = new Map(
  (Object.entries(ProviderRole) as [keyof typeof ProviderRole, ProviderRole][]).map(([key, code]) => [code, key])
);

function getProviderRoleKey(role: ProviderRole | undefined): keyof typeof ProviderRole | undefined {
  return role ? PROVIDER_ROLE_KEY_BY_CODE.get(role) : undefined;
}

/** Abbreviation for a role code, e.g. ProviderRole.PATIENT_ACCESS → "PAC". */
export function getProviderRoleAbbreviation(role: ProviderRole | undefined): string {
  const key = getProviderRoleKey(role);
  return key ? PROVIDER_ROLE_ABBREVIATIONS[key] : '';
}

/** Display label for a role code, e.g. ProviderRole.PATIENT_ACCESS → "Patient Access". */
export function getProviderRoleLabel(role: ProviderRole | undefined): string {
  const key = getProviderRoleKey(role);
  return key ? PROVIDER_ROLE_LABELS[key] : '';
}

export const PROVIDER_ROLE_OPTIONS: ProviderRoleOption[] = (
  Object.entries(ProviderRole) as [keyof typeof ProviderRole, ProviderRole][]
)
  .map(([key, value]) => ({ value, label: PROVIDER_ROLE_LABELS[key] }))
  .sort((a, b) => a.label.localeCompare(b.label));

export const PRIMARY_ROLES: ProviderRole[] = Object.values(ProviderRole).filter(
  (role) => role !== ProviderRole.ADMINISTRATOR
);

/**
 * Roles allowed to create a treatment payment order and send its payment link
 * (BAR-2406, M5).
 *
 * This is the real authorisation boundary for treatment payments, not a UI
 * convenience. `bariendo-be`'s `MedplumApiGuard` is a shared Bearer key, so the
 * backend cannot tell which Orbit user is calling and accepts the acting user
 * only as audit provenance — enforcement has to happen before the call, in the
 * `create-treatment-payment` bot. Keep this list and the bot's gate together;
 * widening it here widens who can charge a patient.
 *
 * FE-1 reads the same list for `canAccessPayments`, so the screen and the bot
 * cannot drift.
 */
export const PAYMENTS_ROLES: ProviderRole[] = [
  ProviderRole.PATIENT_ACCESS,
  ProviderRole.PATIENT_COORDINATOR,
  ProviderRole.PATIENT_NAVIGATOR,
];

/**
 * Roles that may see a patient's payment order, ledger and billing rows without
 * being able to act on them (BAR-2648). Superset of PAYMENTS_ROLES: seeing is
 * never narrower than taking. Enforced for the read operations in the
 * `create-treatment-payment` bot, the same way PAYMENTS_ROLES is for the writes.
 */
export const PAYMENTS_VIEW_ROLES: ProviderRole[] = [...PAYMENTS_ROLES, ProviderRole.FINANCE];

/**
 * Roles that see the Intake hub on /Patient (BAR-2788). Administrators are added
 * by `canViewIntakeHub`. Marketing reads it but not Won, which needs payments (BAR-2813).
 */
export const INTAKE_HUB_ROLES: ProviderRole[] = [
  ProviderRole.PATIENT_NAVIGATOR,
  ProviderRole.PATIENT_ACCESS,
  ProviderRole.PATIENT_COORDINATOR,
  ProviderRole.SUPERVISING_PHYSICIAN,
  ProviderRole.MARKETING,
];

/**
 * Roles that see the Coverage pill on /Patient (BAR-3093): care ops, who record the coverage
 * steps. Administrators are added by `canViewCoverageHub`.
 */
export const COVERAGE_HUB_ROLES: ProviderRole[] = [ProviderRole.PATIENT_COORDINATOR];

export const RESTRICTED_CONFIDENTIALITY_LEVEL_ROLES: ProviderRole[] = [
  ProviderRole.PHYSICIAN,
  ProviderRole.BEHAVIORAL_EXPERT,
  ProviderRole.CARE_MANAGER,
  ProviderRole.NURSE_PRACTITIONER,
  ProviderRole.REGISTERED_DIETITIAN,

  // IMPORTANT: These roles below don't have restricted confidentiality level, we are adding them here to have more open to tackle care team assignment issues
  // Check: https://linear.app/bariendo/document/user-roles-and-access-control-rbac-0bdd4b53f937,  in section: "Temporary version — more open to tackle care team assignment issues"
  ProviderRole.PATIENT_COORDINATOR,
  ProviderRole.PATIENT_NAVIGATOR,
  ProviderRole.SUPERVISING_PHYSICIAN,
];

// Roles that can create/update MedicationRequest (prescriptions)
export const MEDICATION_WRITE_ROLES: ProviderRole[] = [ProviderRole.PHYSICIAN, ProviderRole.NURSE_PRACTITIONER];
// Roles that can place a lab order in Orbit. Care Managers order the pre-procedure panel on
// behalf of an ordering provider; before they could, they ordered in Labcorp's portal, and every
// such result arrived at Health Gorilla unmatched and minted a duplicate Patient (BAR-2845).
export const LABS_WRITE_ROLES: ProviderRole[] = [
  ProviderRole.PHYSICIAN,
  ProviderRole.NURSE_PRACTITIONER,
  ProviderRole.CARE_MANAGER,
];
// Roles whose holders can be the ordering provider (ServiceRequest.requester) on a lab order.
// Health Gorilla also requires an NPI on the Practitioner, checked separately.
export const LAB_ORDERING_PROVIDER_ROLES: ProviderRole[] = [
  ProviderRole.PHYSICIAN,
  ProviderRole.SUPERVISING_PHYSICIAN,
  ProviderRole.NURSE_PRACTITIONER,
];
export const FAX_WRITE_ROLES: ProviderRole[] = [ProviderRole.PATIENT_COORDINATOR];

/**
 * Roles that record employer benefit events — the payor paperwork is the care coordinators'
 * (BAR-2593). BenefitEventWriteAccessPolicy is the boundary; `canRecordBenefitEvents` adds
 * administrators for the UI, and AdminAccessPolicy carries the same grant.
 */
export const BENEFIT_EVENT_WRITE_ROLES: ProviderRole[] = [ProviderRole.PATIENT_COORDINATOR];

/**
 * Roles that may edit a patient's insurance card through the `update-insurance-coverage` bot
 * (BAR-2917): the payments roles, plus Finance. The bot's own policy does the write, so this
 * grants Finance nothing beyond that bot. Same members as PAYMENTS_VIEW_ROLES today, but a
 * separate decision: widening one must not widen the other.
 */
export const INSURANCE_CARD_EDIT_ROLES: ProviderRole[] = [...PAYMENTS_ROLES, ProviderRole.FINANCE];

export const SCHEDULER_MANAGER_ROLES: ProviderRole[] = [
  ProviderRole.PATIENT_COORDINATOR,
  ProviderRole.CARE_MANAGER,
  ProviderRole.ADMINISTRATOR,
  ProviderRole.SUPERVISING_PHYSICIAN,
];

// Roles that can access the Partner Timeline
export const PARTNER_TIMELINE_ROLES: ProviderRole[] = [
  ProviderRole.NURSE_PRACTITIONER, // NP
  ProviderRole.PATIENT_NAVIGATOR, // NN
  ProviderRole.PATIENT_COORDINATOR, // PC
];

/**
 * Roles that may edit the on-call schedule.
 *
 * Everyone else sees it read-only: coverage is something the whole company needs to be able
 * to look up, but only clinical and coordination roles should be able to change who is on
 * call. Reads need no grant — CompartmentAccessPolicy already gives every provider readonly
 * Schedule and Slot.
 *
 * ADMINISTRATOR is listed to state the intent, but neither consumer matches it from here:
 * PRIMARY_ROLES excludes it so getPrimaryProviderRole never returns it, and both the UI
 * predicate and the subscription bot branch on isAdmin() first. Administrators can edit
 * because AdminAccessPolicy already covers Schedule and Slot.
 */
export const ON_CALL_SCHEDULE_EDIT_ROLES: ProviderRole[] = [
  ProviderRole.NURSE_PRACTITIONER,
  ProviderRole.CARE_MANAGER,
  ProviderRole.PATIENT_COORDINATOR,
  ProviderRole.SUPERVISING_PHYSICIAN,
  ProviderRole.ADMINISTRATOR,
];

/** Roles eligible to be configured as on-call NP primary or backup contacts. */
export const ON_CALL_NP_ELIGIBLE_ROLES: ProviderRole[] = [
  ProviderRole.NURSE_PRACTITIONER,
  ProviderRole.CARE_MANAGER,
  ProviderRole.SUPERVISING_PHYSICIAN,
];

export const ConfidentialityLevel = {
  LOW: 'L',
  MODERATE: 'M',
  NORMAL: 'N',
  RESTRICTED: 'R',
  UNRESTRICTED: 'U',
  VERY_RESTRICTED: 'V',
} as const;

export type ConfidentialityLevel = (typeof ConfidentialityLevel)[keyof typeof ConfidentialityLevel];

export const AccessPolicyName = {
  ADMIN: 'AdminAccessPolicy',
  COMPARTMENT_BASED: 'CompartmentAccessPolicy',
  ALL_PATIENTS: 'AllPatientsAccessPolicy',
  ALL_PATIENTS_WRITE: 'AllPatientsWriteAccessPolicy',
  FULL_PATIENTS_LIST: 'FullPatientListAccessPolicy',
  PATIENT_APP: 'PatientAppAccessPolicy',
  MEDICATIONS_WRITE: 'MedicationsWritePolicy',
  LABS_WRITE: 'LabsWritePolicy',
  FAX_WRITE: 'FaxWriteAccessPolicy',
  SCHEDULER: 'SchedulerAccessPolicy',
  SCHEDULER_MANAGER: 'SchedulerManagerAccessPolicy',
  PARTNER_TIMELINE: 'PartnerTimelinePolicy',
  IVR_CONFIG: 'IvrConfigAccessPolicy',
  ON_CALL_SCHEDULE: 'OnCallScheduleAccessPolicy',
  PATIENT_ACCESS: 'CareIntakeAccessPolicy',
  EMERSON_QUALITY_AUDIT: 'EmersonQualityAuditAccessPolicy',
  FINANCE: 'FinanceAccessPolicy',
  BENEFIT_EVENT_WRITE: 'BenefitEventWriteAccessPolicy',
  RESEARCH_FELLOW: 'ResearchFellowAccessPolicy',
  MARKETING: 'MarketingAccessPolicy',
} as const;

export type AccessPolicyName = (typeof AccessPolicyName)[keyof typeof AccessPolicyName];

export const CONFIDENTIALITY_LEVEL_PARAMETER = 'confidentiality-level';
export const PRACTITIONER_SCHEDULE_PARAMETER = 'practitioner_schedule';
export const PRACTITIONER_ROLE_PARAMETER = 'practitioner_role';

export const CommunicationDirection = {
  INCOMING: 'incoming',
  OUTGOING: 'outgoing',
} as const;

export type CommunicationDirection = (typeof CommunicationDirection)[keyof typeof CommunicationDirection];

/**
 * Why an automated SMS was sent (BAR-2656). Carried as `Communication.reasonCode` alongside
 * `Communication.about = [Appointment/<id>]`, which together make a send addressable: the reminder
 * cron dedupes on (appointment, reason) and an inbound Y/N is matched back to the most recent
 * `reminder-*` send rather than guessing from the patient's phone number.
 *
 * Only automated sends carry a reason. A message a human typed in Orbit has none.
 */
export const SmsReason = {
  REMINDER_24H: 'reminder-24h',
  REMINDER_1H: 'reminder-1h',
  REMINDER_3H: 'reminder-3h',
  REMINDER_PROCEDURE_24H: 'reminder-procedure-24h',
  REMINDER_15M: 'reminder-15m',
  REMINDER_START: 'reminder-start',
  NO_SHOW: 'no-show',
  BOOKING_CONFIRMATION: 'booking-confirmation',
  CONFIRMATION_ACK: 'confirmation-ack',
  DECLINE_ACK: 'decline-ack',
} as const;

export type SmsReason = (typeof SmsReason)[keyof typeof SmsReason];

/**
 * Reasons that represent a reminder awaiting a Y/N answer. Acks are not reminders, and neither are
 * the 15-minute and start-time texts (BAR-2687): they ask nothing, so a Y/N sent after one must
 * still resolve to the 1 h or 24 h reminder that asked the question. The booking confirmation
 * (BAR-2784) invites an N to pick a new time, so a reply to it is matched like a reminder's.
 */
export const REMINDER_SMS_REASONS: readonly SmsReason[] = [
  SmsReason.REMINDER_24H,
  SmsReason.REMINDER_1H,
  SmsReason.REMINDER_3H,
  SmsReason.REMINDER_PROCEDURE_24H,
  SmsReason.BOOKING_CONFIRMATION,
];

export const SMS_REASON_SYSTEM = 'http://bariendo.com/fhir/CodeSystem/sms-reason';

/** How a patient answered the Y/N question on an appointment reminder (BAR-2656). */
export const PatientConfirmation = {
  CONFIRMED: 'confirmed',
  DECLINED: 'declined',
} as const;

export type PatientConfirmation = (typeof PatientConfirmation)[keyof typeof PatientConfirmation];

/** The channel the answer arrived on. Only SMS today; kept explicit so a future channel is additive. */
export const PatientConfirmationVia = {
  SMS: 'sms',
} as const;

export type PatientConfirmationVia = (typeof PatientConfirmationVia)[keyof typeof PatientConfirmationVia];

/**
 * How far back a bare "Y" or "N" is still read as an answer to a reminder (BAR-2656). Covers the
 * 24 h reminder plus a day of slack. Past this, the reply is an ordinary message: a patient texting
 * "no" three days later is answering something else, or nothing. The one exception is a booking
 * confirmation, which stays answerable until its appointment starts (BAR-2784).
 */
export const CONFIRMATION_REPLY_WINDOW_HOURS = 48;

/** Appointment statuses that can still be confirmed or declined. */
export const CONFIRMABLE_APPOINTMENT_STATUSES = ['booked', 'arrived', 'checked-in'] as const;

/** Languages patient-facing SMS is written in. Mirrors bariendo-be's `Language` enum values. */
export const SmsLanguage = {
  EN: 'en',
  ES: 'es',
} as const;

export type SmsLanguage = (typeof SmsLanguage)[keyof typeof SmsLanguage];

export const CommunicationCategory = {
  PHONE_CALL: 'phone-call',
  SMS_MESSAGE: 'sms-message',
  TIMELINE_NOTE: 'timeline-note',
  STANDALONE_NOTE: 'standalone-note',
  CHAT: 'chat',
  THREAD: 'thread',
  VIRTUAL_RECEPTIONIST_FORWARD: 'virtual-receptionist-forward',
  SALES_TEAM_FORWARD: 'sales-team-forward',
  PARTNER_CALL: 'partner-call',
  LAB_ORDER: 'lab-order',
  ENCOUNTER: 'encounter',
  INBOUND: 'inbound',
  /** Outbound fax (paired with medium telefax); queryable alongside other timeline categories */
  FAX_MESSAGE: 'fax-message',
  /**
   * Patient Access intro-call chart note — the guided call-script card. Also
   * carries the PN consultation (Intake 1.1) as a third visit type; a category
   * of its own is deferred, so PN consultations count with intro calls for now.
   */
  PAC_INTRO_CALL_NOTE: 'ci-intro-call-note',
  /** Patient Access warm hand-off chart note — the call after the NP consult. */
  PAC_WARM_HANDOFF_NOTE: 'ci-warm-handoff-note',
} as const;

// These two live here, not with their readers, so `deploy/bots-registry.ts` can
// import them: deploy-bots runs under Node's ESM loader, which cannot resolve
// the extensionless relative imports in most lib files.

/** The Last communication categories — calls, texts and chat, not notes. */
export const LAST_COMMUNICATION_CATEGORIES = [
  CommunicationCategory.PHONE_CALL,
  CommunicationCategory.SMS_MESSAGE,
  CommunicationCategory.CHAT,
];

/** Every intake-card category, as one search token. */
export const PAC_CALL_NOTE_CATEGORIES = [CommunicationCategory.PAC_INTRO_CALL_NOTE] as const;

export const CONFIDENTIAL_COMMUNICATION_CATEGORIES: CommunicationCategory[] = [
  CommunicationCategory.TIMELINE_NOTE,
  CommunicationCategory.STANDALONE_NOTE,
  CommunicationCategory.CHAT,
  CommunicationCategory.SMS_MESSAGE,
  CommunicationCategory.INBOUND,
  CommunicationCategory.FAX_MESSAGE,
  CommunicationCategory.PAC_INTRO_CALL_NOTE,
  CommunicationCategory.PAC_WARM_HANDOFF_NOTE,
];

export type CommunicationCategory = (typeof CommunicationCategory)[keyof typeof CommunicationCategory];

export const TimelineFilterType = {
  ALL: 'all',
  COMMUNICATIONS: 'communications',
  DOCUMENTATION: 'documentation',
  CARE_CLINICAL: 'care-clinical',
  APPOINTMENT: 'appointment',
  // Payment, refund and employer benefit cards (BAR-2646). Gated on canViewPayments.
  BILLING: 'billing',
} as const;

export type TimelineFilterType = (typeof TimelineFilterType)[keyof typeof TimelineFilterType];

export const COMMUNICATION_MAIN_CATEGORIES: CommunicationCategory[] = [
  CommunicationCategory.PHONE_CALL,
  CommunicationCategory.SMS_MESSAGE,
  CommunicationCategory.TIMELINE_NOTE,
  CommunicationCategory.STANDALONE_NOTE,
  CommunicationCategory.CHAT,
  CommunicationCategory.THREAD,
  CommunicationCategory.INBOUND,
  CommunicationCategory.FAX_MESSAGE,
  CommunicationCategory.PAC_INTRO_CALL_NOTE,
  CommunicationCategory.PAC_WARM_HANDOFF_NOTE,
];

export const COMMUNICATION_SECONDARY_CATEGORIES: CommunicationCategory[] = [
  CommunicationCategory.VIRTUAL_RECEPTIONIST_FORWARD,
  CommunicationCategory.SALES_TEAM_FORWARD,
];

export type CommunicationForwardCategory =
  | typeof CommunicationCategory.VIRTUAL_RECEPTIONIST_FORWARD
  | typeof CommunicationCategory.SALES_TEAM_FORWARD;

export const CommunicationThreadTopic = {
  LOGISTICAL: 'logistical_thread',
  CLINICAL: 'clinical_thread',
  NUTRITION: 'nutrition_thread',
} as const;

export type CommunicationThreadTopic = (typeof CommunicationThreadTopic)[keyof typeof CommunicationThreadTopic];

/** Patient-facing labels for in-app chat thread types (Orbit + patient app). */
export const COMMUNICATION_THREAD_TOPIC_PATIENT_LABELS: Record<CommunicationThreadTopic, string> = {
  [CommunicationThreadTopic.LOGISTICAL]: 'Scheduling & support',
  [CommunicationThreadTopic.CLINICAL]: 'Clinical team',
  [CommunicationThreadTopic.NUTRITION]: 'Dietitian team',
};

export const CommunicationMedium = {
  PHONE_CALL: 'PHONE',
  VIDEO_CALL: 'VIDEOCONF',
  SMS: 'SMSWRIT',
  MMS: 'MMSWRIT',
  APP: 'APPWRIT',
  EMAIL: 'EMAILWRIT',
  FAX: 'FAXWRIT',
  CHAT: 'ONLINEWRIT',
} as const;

export type CommunicationMedium = (typeof CommunicationMedium)[keyof typeof CommunicationMedium];

export const NotificationStatus = {
  UNREAD: 'in-progress',
  READ: 'on-hold',
  ARCHIVED: 'completed',
} as const;

export type NotificationStatus = (typeof NotificationStatus)[keyof typeof NotificationStatus];

export const CareTeamRole = {
  CALLABLE: 'callable',
} as const;

export type CareTeamRole = keyof typeof CareTeamRole;

export const LocationType = {
  HEADQUARTERS: 'headquarters',
  PROCEDURE_INSTITUTION: 'procedure-institution',
} as const;

export type LocationType = (typeof LocationType)[keyof typeof LocationType];

export const BariatricProcedureStage = {
  PRE_CONSULTATION_UNPAID: 'pre-consultation-unpaid',
  PRE_CONSULTATION_PAID: 'pre-consultation-paid',
  POST_CONSULTATION: 'post-consultation',
  /** Waiting on money: an issued, unpaid service order (BAR-2824). */
  FINANCING_APPROVAL: 'financing-approval',
  PRE_PROCEDURE: 'pre-procedure',
  POST_PROCEDURE: 'post-procedure',
  REFUNDED: 'refunded',
  SUBSCRIPTION_ACTIVE: 'subscription-active',
  UNKNOWN: 'unknown',
} as const;

export type BariatricProcedureStage = (typeof BariatricProcedureStage)[keyof typeof BariatricProcedureStage];

export const BariatricProcedureStageLabel: Record<BariatricProcedureStage, string> = {
  [BariatricProcedureStage.PRE_CONSULTATION_UNPAID]: 'Inquiry',
  [BariatricProcedureStage.PRE_CONSULTATION_PAID]: 'Pre-consultation',
  [BariatricProcedureStage.POST_CONSULTATION]: 'Post-consultation',
  [BariatricProcedureStage.FINANCING_APPROVAL]: 'Financing and approval',
  [BariatricProcedureStage.PRE_PROCEDURE]: 'Pre-procedure',
  [BariatricProcedureStage.POST_PROCEDURE]: 'Post-procedure',
  // The code stays `refunded` for Customer.io segments; the stage reads Cancelled (BAR-3083).
  [BariatricProcedureStage.REFUNDED]: 'Cancelled',
  [BariatricProcedureStage.SUBSCRIPTION_ACTIVE]: 'Medication Patient',
  [BariatricProcedureStage.UNKNOWN]: 'Unknown',
};

export const OrganizationType = {
  FACILITY: '22232009',
  PHARMACY: '264372000',
  LABORATORY: '261904005',
  SUPPLIER: '774164004',
  INSURANCE: '724711000000101',
  EXTERNAL_PROVIDER: '719031000000103',
  IV_HYDRATION_PARTNER: '428191000124101',
  ANESTHESIA: '394577000',
} as const;

export type OrganizationType = (typeof OrganizationType)[keyof typeof OrganizationType];

/**
 * Task Group Identifiers categorize tasks by their functional purpose in the system.
 *
 * When defining a new identifier:
 * 1. Use kebab-case for the value (e.g., 'my-new-group')
 * 2. Use SCREAMING_SNAKE_CASE for the constant key
 * 3. Add the identifier to Task.groupIdentifier.coding[].code in FHIR resources
 * 4. Document the use case below
 *
 * Current identifiers and their uses:
 * - GENERAL_NOTIFICATION: General inbox notifications that require provider attention
 * - RESOURCE_ASSIGNMENT: Tasks created when resources (Communications, etc.) are assigned to a provider
 * - CONVERSATION_LEAD: Tasks tracking conversation lead responsibilities for patient care coordination
 * - PROVIDER_MENTION: Tasks created when a provider is @mentioned in chat/notes
 */
export const TaskGroupIdentifier = {
  GENERAL_NOTIFICATION: 'general-notification',
  RESOURCE_ASSIGNMENT: 'resource-assignment',
  CONVERSATION_LEAD: 'conversation-lead',
  PROVIDER_MENTION: 'provider-mention',
  ENCOUNTER: 'encounter',
  ORBIT_PROVIDER_TASK: 'orbit-provider-task',
} as const;

export type TaskGroupIdentifier = (typeof TaskGroupIdentifier)[keyof typeof TaskGroupIdentifier];

export const TaskStatus = {
  READY: 'ready',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  /** Soft delete for clinical tasks: keeps the audit trail, excluded from every open-task query. */
  CANCELLED: 'cancelled',
} as const;

export type TaskStatus = (typeof TaskStatus)[keyof typeof TaskStatus];

export const HEALTHIE_SYSTEM_URL = 'https://www.gethealthie.com/';
export const HEALTHIE_LAB_TEST_CODE_SYSTEM = HEALTHIE_SYSTEM_URL + 'lab-test-codes';

// HealthGorilla NPI system for lab orders
export const US_NPI_SYSTEM = 'http://hl7.org/fhir/sid/us-npi';

// Bariendo test group system
export const BARIENDO_TEST_GROUP_SYSTEM = 'http://bariendo.com/fhir/extension/test-group';
export const APPOINTMENT_SERIES_IDENTIFIER_SYSTEM = 'http://bariendo.com/fhir/identifier/appointment-series';

/**
 * Marks a phantom provider: a Practitioner with no person behind it and no login,
 * booked to overbook PN consults (BAR-2866). bariendo-be's Zoom webhook uses the same
 * system to announce a patient waiting in a phantom visit.
 */
export const PHANTOM_PROVIDER_IDENTIFIER_SYSTEM = 'http://bariendo.com/fhir/identifier/phantom-provider';

/**
 * PractitionerRole code of a phantom PN. Deliberately not a `ProviderRole`: every PN
 * pool (call routing, message assignment, "assigned PN") filters on the real PN code,
 * so a phantom carrying only this one is invisible to all of them. Only the
 * `pn-consultation-visit` ActivityDefinition lists it, and bariendo-be's
 * APPOINTMENT_TYPE_TO_ROLES mirrors that.
 */
export const PHANTOM_PATIENT_NAVIGATOR_ROLE_CODE = 'phantom-patient-navigator';

/**
 * `ActivityDefinition.useContext` code (in `BARIENDO_CT_SYSTEM`) for an overflow
 * role (BAR-3101): one that may take the appointment type, but is offered only to
 * a caller that names it. Only the $book check and the availability editor's
 * option list count it (`isOverflowRoleContext`); Orbit's slot grid, hold-slot and
 * the appointment drawer do not. NPs overflow into the PN consultation, and only
 * bariendo-be names them — for employer and insurance patients — mirroring its
 * APPOINTMENT_TYPE_TO_OVERFLOW_ROLES.
 */
export const OVERFLOW_PRACTITIONER_ROLE_CONTEXT_CODE = 'overflow-practitioner-role';

/**
 * Marks the one clinical Task announcing that a patient settled their treatment
 * payment, keyed by Patient id. It exists to make creation idempotent: the
 * bot that writes it (`update-procedure-status`) is re-invoked for a patient
 * whenever bariendo-be sees an appointment change, and by the Update Patient
 * States admin page, so "the patient is paid" is not on its own evidence that
 * the payment is news. Searched across ALL statuses — a task the team already
 * completed must not come back.
 */
export const SERVICE_PAYMENT_TASK_IDENTIFIER_SYSTEM = 'http://bariendo.com/fhir/identifier/service-payment-task';

/**
 * Identifier system for non-human service accounts — Practitioners that exist only so an automated
 * send has an attributable, filterable sender. Not a real provider; never staffed, never on a care
 * team.
 */
export const SERVICE_ACCOUNT_IDENTIFIER_SYSTEM = 'http://bariendo.com/fhir/identifier/service-account';

/** The service account that sends appointment reminder and no-show SMS (BAR-2627). */
export const APPOINTMENT_REMINDER_SERVICE_ACCOUNT = 'appointment-reminders';

/**
 * The service account Customer.io-sent SMS are attributed to (BAR-2715), so the Orbit timeline reads
 * "Customer.io" rather than "Unknown sender" or a reminder bot that did not send them.
 */
export const CUSTOMERIO_SERVICE_ACCOUNT = 'customerio';

/** Identifier system for a Communication recorded from a Customer.io delivery; value = delivery id. */
export const CUSTOMERIO_DELIVERY_SYSTEM = 'https://customer.io/delivery';

/**
 * Zoom recurrence types (for the `recurrence.type` field in Zoom meeting create).
 * Zoom: 1 = daily, 2 = weekly, 3 = monthly.
 */
export const ZoomRecurrenceType = {
  DAILY: 1,
  WEEKLY: 2,
  MONTHLY: 3,
} as const;

export type ZoomRecurrenceType = (typeof ZoomRecurrenceType)[keyof typeof ZoomRecurrenceType];

export function isZoomRecurrenceType(value: unknown): value is ZoomRecurrenceType {
  return (
    value === ZoomRecurrenceType.DAILY || value === ZoomRecurrenceType.WEEKLY || value === ZoomRecurrenceType.MONTHLY
  );
}

// Flag categories for real-time availability, plus the PAC lead-status override
export const FlagCategory = {
  PROVIDER_REAL_TIME_AVAILABILITY: 'provider-real-time-availability',
  PROVIDER_IN_CALL: 'provider-in-call',
  /**
   * A PAC rep's manual Active-lead switch-off. A Flag rather than a Patient
   * extension or a meta.tag because `Flag.author` and `Flag.period.start` carry
   * the who and when that the reason line needs.
   */
  PAC_LEAD_STATUS: 'ci-lead-status',
} as const;

export type FlagCategory = (typeof FlagCategory)[keyof typeof FlagCategory];

// Flag codes for availability status
export const ProviderAvailabilityStatus = {
  ONLINE: 'online',
  DO_NOT_DISTURB: 'do-not-disturb',
} as const;

export type ProviderAvailabilityStatus = (typeof ProviderAvailabilityStatus)[keyof typeof ProviderAvailabilityStatus];

export const FEATURE_FLAGS = {
  MAINTENANCE_MODE: 'maintenance-mode',
  ENABLE_DOSESPOT_CONFIG: 'enable-dosespot-config',
  ENABLE_PATIENT_CENTRAL: 'enable-patient-central',
  ENABLE_PATIENT_LIST_ALL_FILTERS: 'enable-patient-list-all-filters',
  ENABLE_MULTI_PARTY_CALLING: 'enable-multi-party-calling',
  ENABLE_TASK_MANAGEMENT: 'enable-task-management',
  ENABLE_IVR_CONFIGURATION: 'enable-ivr-configuration',
  ENABLE_SIGNUPS_TRACKER: 'enable-signups-tracker',
  ENABLE_TREATMENT_PAYMENTS: 'enable-treatment-payments',
  ENABLE_PATIENT_EXPORT: 'enable-patient-export',
  ENABLE_CI_MANAGER_DASHBOARD: 'enable-ci-manager-dashboard',
  ENABLE_INTAKE_BILLING_PANEL: 'enable-intake-billing-panel',
  ENABLE_EMPLOYER_BENEFIT_EVENTS: 'enable-employer-benefit-events',
  ENABLE_INTAKE_HUB: 'enable-intake-hub',
  /** The Insurance section under Intake and billing in the patient info panel (BAR-2931). */
  ENABLE_INSURANCE_SECTION: 'enable-insurance-section',
  /** The PN consultation card v2.0: one card for every track (BAR-2994). Off, the shipped card. */
  ENABLE_PN_CARD_V2: 'enable-pn-card-v2',
  /** The Finance ops page, for Finance and Admin (BAR-3024). */
  ENABLE_FINANCE_OPS: 'enable-finance-ops',
  /** The Coverage pill on /Patient, care ops' worklist (BAR-3093). */
  ENABLE_COVERAGE_HUB: 'enable-coverage-hub',
} as const;

export type FeatureFlag = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

/**
 * Flags rolled out to 100% of production users for over a month, on their way to deletion.
 * They read as enabled whenever PostHog does not report them — which covers the window
 * before flags load and PostHog being unreachable, so a shipped feature no longer flickers
 * off or disappears entirely when the key is missing.
 *
 * Listing a flag here makes it one-way: the provider only ever sees PostHog's *active* flag
 * list, so turning one of these off in PostHog no longer disables it. That is deliberate for
 * this step — the next step drops the check at each call site and deletes the flag. Never
 * list a flag that is still being rolled out.
 */
export const DEFAULT_ENABLED_FEATURE_FLAGS: ReadonlySet<FeatureFlag> = new Set([
  FEATURE_FLAGS.ENABLE_PATIENT_LIST_ALL_FILTERS,
  FEATURE_FLAGS.ENABLE_MULTI_PARTY_CALLING,
  FEATURE_FLAGS.ENABLE_SIGNUPS_TRACKER,
]);

export const LAB_ORDER_QUESTIONNAIRE_IDENTIFIER = 'http://bariendo.com/fhir/identifier/questionnaire|lab-order-general';
export const HEALTHGORILLA_SEND_LAB_ORDER_BOT_IDENTIFIER: Identifier = {
  system: 'https://www.medplum.com/integrations/bot-identifier',
  value: 'health-gorilla-labs/send-to-health-gorilla',
};

export const HEALTHGORILLA_IDENTIFIER_SYSTEM = 'https://www.healthgorilla.com';
export const HEALTHGORILLA_TENANT_IDENTIFIER_SYSTEM = `${HEALTHGORILLA_IDENTIFIER_SYSTEM}/tenantId`;
export const HEALTHGORILLA_LABCORP_ACCOUNT_NUMBER_IDENTIFIER_SYSTEM =
  'http://bariendo.com/fhir/identifier/labcorp-account-number';

export const DOSESPOT_PRESCRIPTIONS_SYNC_BOT_IDENTIFIER: Identifier = {
  system: 'https://www.medplum.com/bots',
  value: 'dosespot-prescriptions-sync-bot',
};

/**
 * Encounter status constants used for frontend logic.
 * Note: This contains custom statuses (e.g., 'editing') that are not part of the FHIR Encounter['status'] typing.
 * These are used for UI state management and styling purposes.
 */
export const EncounterStatus = {
  PLANNED: 'planned',
  ARRIVED: 'arrived',
  TRIAGED: 'triaged',
  IN_PROGRESS: 'in-progress',
  ON_LEAVE: 'onleave',
  FINISHED: 'finished',
  CANCELLED: 'cancelled',
  EDITING: 'editing',
} as const;

export type EncounterStatus = (typeof EncounterStatus)[keyof typeof EncounterStatus];

export const CHARTING_EXPORTED_PDF_TIMEZONE = 'America/New_York' as const;

/**
 * Schedule categories to distinguish different types of provider availability schedules.
 * Used in Schedule.serviceCategory to categorize schedules.
 *
 * - CALL_AVAILABILITY: Schedules that define when a provider is available to receive phone calls
 * - APPOINTMENT_AVAILABILITY: Schedules that define when a provider is available for booking appointments
 * - IVR_BUSINESS_HOURS: Schedules that define IVR business hours for the headquarters location
 * - ON_CALL_NP: Schedules that define on-call NP coverage shifts for the headquarters location
 */
export const ScheduleCategory = {
  CALL_AVAILABILITY: 'call-availability',
  APPOINTMENT_AVAILABILITY: 'appointment-availability',
  IVR_BUSINESS_HOURS: 'ivr-business-hours',
  ON_CALL_NP: 'on-call-np',
} as const;

/**
 * On-call defaults are policy stated in Eastern Time, not in whoever's timezone is looking
 * at the table. They are converted for display by getDefaultOnCallTimes.
 */
export const ON_CALL_POLICY_TIMEZONE = 'America/New_York';

/** Weekends run a full 24 hours; weekdays start at the end of the business day. */
export const ON_CALL_WEEKEND_DEFAULT_START_TIME = '08:30';
export const ON_CALL_WEEKEND_DEFAULT_END_TIME = '08:30';
export const ON_CALL_WEEKDAY_DEFAULT_START_TIME = '18:00';
export const ON_CALL_WEEKDAY_DEFAULT_END_TIME = '08:30';

/**
 * @deprecated Weekday defaults, kept because the IVR hours drawer compares a set's closing
 * time against roughly when on-call begins. Prefer getDefaultOnCallTimes, which is
 * day-of-week aware and timezone-correct.
 */
export const DEFAULT_ON_CALL_START_TIME = ON_CALL_WEEKDAY_DEFAULT_START_TIME;
export const DEFAULT_ON_CALL_END_TIME = ON_CALL_WEEKDAY_DEFAULT_END_TIME;

/**
 * The editor shows the rest of the current month plus this many whole months after it.
 *
 * Widening this multiplies both the rows rendered and the slots fetched, so the editor's
 * Slot search must stay bounded and paged — see ensureOnCallSchedule.
 */
export const ON_CALL_SCHEDULE_MONTHS_AHEAD = 2;

/** How far the stats month picker reaches either side of the current month. */
export const ON_CALL_STATS_MONTHS_BACK = 6;
export const ON_CALL_STATS_MONTHS_FORWARD = 3;

export type ScheduleCategory = (typeof ScheduleCategory)[keyof typeof ScheduleCategory];

export const AppointmentChangeEventType = {
  CREATED: 'appointment-created',
  CANCELLED: 'appointment-cancelled',
  UPDATED: 'appointment-updated',
};

export type AppointmentChangeEventType = (typeof AppointmentChangeEventType)[keyof typeof AppointmentChangeEventType];

export const AppointmentStatus = {
  PROPOSED: 'proposed',
  PENDING: 'pending',
  BOOKED: 'booked',
  ARRIVED: 'arrived',
  FULFILLED: 'fulfilled',
  CANCELLED: 'cancelled',
  NOSHOW: 'noshow',
  ENTERED_IN_ERROR: 'entered-in-error',
  CHECKED_IN: 'checked-in',
  WAITLIST: 'waitlist',
} as const;

export const APPOINTMENT_STATUS_RESCHEDULED = 'rescheduled' as const;

export const APPOINTMENT_STATUS_OPTIONS = [
  { value: AppointmentStatus.BOOKED, label: 'Scheduled' },
  { value: AppointmentStatus.FULFILLED, label: 'Occurred' },
  { value: AppointmentStatus.NOSHOW, label: 'No show' },
  { value: APPOINTMENT_STATUS_RESCHEDULED, label: 'Rescheduled' },
  { value: AppointmentStatus.CANCELLED, label: 'Cancelled' },
] as const;

export const IV_THERAPY_APPOINTMENT_TYPE_CODE = 'iv-therapy' as const;

// One-off calendar blocks (provider-only reservations without a patient visit)
export const BLOCK_APPOINTMENT_TYPE_CODE = 'calendar-block' as const;

export const ALLOWED_UPLOAD_DOCUMENT_CONTENT_TYPES = new Set([
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/rtf',
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/tiff',
  'image/bmp',
  'image/webp',
  'image/heic',
  'image/heif',
  'message/rfc822', // .eml
]);

export const PATIENT_PAGE_FILTER_OPTIONS = {
  ALL: 'all',
  MY_PATIENTS: 'my-patients',
  MY_UPCOMING_APPOINTMENTS: 'my-upcoming-appointments',
  ALL_INQUIRY: 'all-inquiry',
  ALL_PRE_CONSULTATION: 'all-pre-consultation',
  ALL_POST_CONSULTATION: 'all-post-consultation',
  ALL_PRE_PROCEDURE: 'all-pre-procedure',
  ALL_POST_PROCEDURE: 'all-post-procedure',
  CARRUM: 'carrum',
  INTAKE: 'intake',
  COVERAGE: 'coverage',
} as const;

export type PatientPageFilterOption = (typeof PATIENT_PAGE_FILTER_OPTIONS)[keyof typeof PATIENT_PAGE_FILTER_OPTIONS];

export const IVR_BASIC_SYSTEM = 'http://bariendo.com/fhir/CodeSystem/ivr';
export const IVR_TEMPLATE_IDENTIFIER_SYSTEM = 'http://bariendo.com/fhir/identifier/ivr-template';
export const IVR_HOURS_SET_IDENTIFIER_SYSTEM = 'http://bariendo.com/fhir/identifier/ivr-hours-set';

/**
 * Named business-hours sets. A menu option references one by id so that teams with
 * different closing times can be configured independently: clinical options fall
 * through to after-hours behaviour earlier than intake options do.
 *
 * Each set is a Schedule carrying the IVR_BUSINESS_HOURS service category, discriminated
 * by IVR_HOURS_SET_IDENTIFIER_SYSTEM. An unidentified legacy Schedule is read as INTAKE,
 * which has the later hours and therefore preserves today's company-wide behaviour.
 */
export const IvrHoursSetId = {
  CLINICAL: 'clinical',
  INTAKE: 'intake',
} as const;

export type IvrHoursSetId = (typeof IvrHoursSetId)[keyof typeof IvrHoursSetId];

export const IVR_HOURS_SET_DEFAULT_NAMES: Record<IvrHoursSetId, string> = {
  [IvrHoursSetId.CLINICAL]: 'Clinical',
  [IvrHoursSetId.INTAKE]: 'Intake',
};

export const IvrTemplateId = {
  BUSINESS_HOURS_EN: 'business-hours-en',
  BUSINESS_HOURS_ES: 'business-hours-es',
  OFF_HOURS_EN: 'off-hours-en',
  OFF_HOURS_ES: 'off-hours-es',
} as const;

export type IvrTemplateId = (typeof IvrTemplateId)[keyof typeof IvrTemplateId];

/**
 * Templates a caller can actually reach.
 *
 * Since BAR-2473 the greeting no longer varies by time of day — every caller hears the
 * same menu and hours resolve per option after a digit is pressed — so the OFF_HOURS_*
 * templates are unreachable. They stay in IvrTemplateId so existing Basic resources still
 * parse; retiring the ids themselves needs a data migration.
 */
export const IVR_ACTIVE_TEMPLATE_IDS: IvrTemplateId[] = [
  IvrTemplateId.BUSINESS_HOURS_EN,
  IvrTemplateId.BUSINESS_HOURS_ES,
];

export function isActiveIvrTemplateId(value: string | undefined): value is IvrTemplateId {
  return !!value && (IVR_ACTIVE_TEMPLATE_IDS as string[]).includes(value);
}

/**
 * What each template is called in the config UI.
 *
 * The stored `name` on every template Basic still reads "Business Hours — …", which stopped
 * being true once the greeting stopped varying by time of day. Language is now the only
 * thing that distinguishes them, so that is all the label says. Rewriting the stored names
 * needs a data migration; this keeps the UI honest until then.
 */
export const IVR_TEMPLATE_DISPLAY_NAMES: Record<IvrTemplateId, string> = {
  [IvrTemplateId.BUSINESS_HOURS_EN]: 'English',
  [IvrTemplateId.BUSINESS_HOURS_ES]: 'Spanish',
  [IvrTemplateId.OFF_HOURS_EN]: 'English (retired)',
  [IvrTemplateId.OFF_HOURS_ES]: 'Spanish (retired)',
};

/**
 * @deprecated Superseded by {@link IvrTargetKind} + {@link IvrTargetScope} (BAR-2619).
 *
 * Kept because it is still the code written to `IVR_ROUTING_ACTION_TYPE_EXTENSION`: a key
 * mapping serialises its chain as rungs *and* as the legacy single-action group, so a bot
 * running the previous release keeps routing. Removed once BAR-2621 ships the chain-walking
 * runtime and the legacy group can stop being written.
 */
export const IvrRoutingActionType = {
  ROLE_POOL: 'role-pool',
  EXTERNAL_NUMBER: 'external-number',
  SPECIFIC_PROVIDER: 'specific-provider',
  CARE_TEAM_BY_ROLE: 'care-team-by-role',
  ON_CALL_NP: 'on-call-np',
  PLAY_AND_HANGUP: 'play-and-hangup',
} as const;

export type IvrRoutingActionType = (typeof IvrRoutingActionType)[keyof typeof IvrRoutingActionType];

/**
 * One rung of a routing chain.
 *
 * `ROLE` absorbs both legacy role action types; which of the two a rung means is carried by
 * {@link IvrTargetScope} rather than by the kind, so an option can mix scopes down the chain
 * ("PN owner, then PAC pool") — the thing the single-backup model could not express.
 */
export const IvrTargetKind = {
  ROLE: 'role',
  ON_CALL_NP: 'on-call-np',
  EXTERNAL_NUMBER: 'external-number',
  SPECIFIC_PROVIDER: 'specific-provider',
  PLAY_AND_HANGUP: 'play-and-hangup',
} as const;

export type IvrTargetKind = (typeof IvrTargetKind)[keyof typeof IvrTargetKind];

/**
 * Which providers holding a role a `role` rung rings.
 *
 * `POOL` is everyone with the role (the old `role-pool`); `OWNER` is the members of *this
 * patient's* care team holding it (the old `care-team-by-role`).
 */
export const IvrTargetScope = {
  POOL: 'pool',
  OWNER: 'owner',
} as const;

export type IvrTargetScope = (typeof IvrTargetScope)[keyof typeof IvrTargetScope];

/**
 * Why a routing candidate was passed over, recorded on the call so a decision can be
 * explained afterwards (BAR-2621).
 *
 * Codes, not sentences: they are written to FHIR and read back by surfaces that will word
 * them themselves. The first three mirror the three gates in `checkProviderAvailability`.
 */
export const CallRoutingSkipReason = {
  /** Holding an active in-call Flag when the rung was evaluated. */
  IN_ANOTHER_CALL: 'in-another-call',
  /** Do-not-disturb set on their real-time availability Flag. */
  DO_NOT_DISTURB: 'do-not-disturb',
  /** No schedule, a busy slot, or outside every availability window. */
  OFF_SHIFT: 'off-shift',
  /** Rang for the full rung timeout without picking up. */
  NO_ANSWER: 'no-answer',
  /** Already offered this call earlier in the chain. */
  ALREADY_TRIED: 'already-tried',
  /** The rung resolved to nobody at all — skipped without ringing. */
  NO_ELIGIBLE_PROVIDER: 'no-eligible-provider',
  /** Selected, but no phone number to dial (on-call rota rungs dial a real handset). */
  NO_PHONE_NUMBER: 'no-phone-number',
  /** An `on-call-np` rung with no rota slot covering the current time. */
  NO_ON_CALL_SLOT: 'no-on-call-slot',
  /** An `owner`-scoped rung on a call from someone who is not a known patient. */
  NO_PATIENT_CONTEXT: 'no-patient-context',
} as const;

export type CallRoutingSkipReason = (typeof CallRoutingSkipReason)[keyof typeof CallRoutingSkipReason];
